import { useMemo, useRef, useEffect, useState, Suspense, ReactNode } from 'react';
import { Spin, message } from 'antd';
import { useContextMenu } from 'react-contexify';
import type { DialogueRefObject } from 'components/Dialogue/types';
import useModal from 'hooks/useModal';
import { noop } from 'lodash-es';
import { LocalMessage } from 'hooks/business/conversation';
import { useNimStatus } from 'components/Nim';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import sendConvOpe from 'services/nim/toSystem/sendConvOpe';
import { useAuthorization } from 'components/Authorization';
import { useMemoizedFn } from 'ahooks';
import ContextMenu, { contextMenuId, ContextMenuSingleType } from '../MessageMenu/ContextMenu';
import useChatView, { UIMessages } from '../../useChatView';
import Feedback from '../Feedback';
import { ChatProvider } from '../../contexts/ChatContext';
import { useChatConversationContext } from '../../contexts/ChatConversationContext';
import { useTranslate } from '@tolgee/react';
import { SimpleChat } from './SimpleChat';
import { AdvancedChat } from './AdvancedChat';
import { MediaPreviewProvider } from '../MediaPreview/useMediaPreview';
import { useConversationTopBarInfoLoader } from 'hooks/business/conversationTopBarInfo';
import { useConversationKnowledgeQueryLoader } from 'hooks/business/conversationKnowledgeQuery';

export interface ChatMainProps {
  conversationId: Conversation.Conversation['conversationId'];
  className?: string;
  // 是否只渲染消息列表
  isGalaxy?: boolean;
  preventPosScroll?: boolean;
  targetMessageExtra?: ReactNode;
  getDialogueRef?: (dialogueRef: DialogueRefObject | null) => void;
  showSideFoldBtn?: boolean;
  iframeMode?: boolean;
  forceAcquireControlReason?: Conversation.ForceAcquireControlReason;
  filterMessages?: string[];
  galaxyModule?: Conversation.GalaxyModule;
  highlightMsg?: Record<string, string>;
  primaryKey?: string;
}

const noopFalse = () => false;

const ChatMainWithoutMediaContext = (props: ChatMainProps) => {
  const {
    className,
    isGalaxy = false,
    targetMessageExtra,
    getDialogueRef,
    showSideFoldBtn,
    iframeMode,
    galaxyModule,
    forceAcquireControlReason,
    preventPosScroll = false,
    filterMessages = [],
    highlightMsg,
    primaryKey,
  } = props;
  const auth = useAuthorization<Oversea.Token.Payload>();
  const { t } = useTranslate();

  const { currentConversation: conversation } = useChatConversationContext();
  const { singleOrBatchTrans } = useChatView(iframeMode, undefined, false, !isGalaxy);
  const dialogueRef = useRef<DialogueRefObject>({
    dom: null,
    scrollTo: noop,
    scrollToMessage: noop,
    scrollToMessageByScrollTo: noopFalse,
  });
  const hasQAPairMountedRef = useRef(false);

  const sysLang = useSystemLanguageValue();
  const nimStatus = useNimStatus();

  const {
    visible: feedbackVisible,
    show: showFeedback,
    hide: hideFeedback,
    meta: feedbackMessage,
  } = useModal<LocalMessage>();

  if (conversation?.localError) {
    throw new Error(
      JSON.stringify({
        conversationId: conversation.conversationId,
        error: conversation.localError,
      }),
    );
  }

  const [contextMenuMessage, setContextMenuMessage] = useState<UIMessages>();
  const [contextMenuSingleType, setContextMenuSingleType] = useState<ContextMenuSingleType>();

  const [videoTextState, setVideoTextState] = useState<any>();

  const { show: showContextMenu } = useContextMenu({
    id: contextMenuId,
  });

  const refsCurrent = {
    showContextMenu,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  // 引用消息点击
  const handleQuoteClick = useMemoizedFn((msgId: string) => {
    const result = dialogueRef.current?.scrollToMessageByScrollTo(msgId);
    if (!result) {
      message.warning(t('ViewsChat.未找到该消息'));
    }
  });

  const contextMenu = useMemo(() => {
    if (contextMenuMessage === undefined || conversation === undefined) {
      return null;
    }

    return (
      <ContextMenu
        uiMessage={contextMenuMessage}
        conversation={conversation}
        isGalaxy={isGalaxy}
        onTranslate={singleOrBatchTrans}
        onFeedback={showFeedback}
        singleType={contextMenuSingleType}
        targetLanguage={sysLang}
      />
    );
  }, [
    contextMenuMessage,
    conversation,
    isGalaxy,
    contextMenuSingleType,
    sysLang,
    singleOrBatchTrans,
    showFeedback,
  ]);

  const setDialogRef = useMemoizedFn((value: DialogueRefObject | null) => {
    if (dialogueRef.current) {
      dialogueRef.current.dom = null;
      dialogueRef.current.scrollTo = noop;
      dialogueRef.current.scrollToMessage = noopFalse;
    }

    if (value) {
      dialogueRef.current.dom = value.dom;
      dialogueRef.current.scrollTo = value.scrollTo;
      dialogueRef.current.scrollToMessage = value.scrollToMessage;
      dialogueRef.current.scrollToMessageByScrollTo = value.scrollToMessageByScrollTo;
    }

    getDialogueRef?.(dialogueRef.current);
  });

  const setHasQAPairMounted = useMemoizedFn((value: boolean) => {
    hasQAPairMountedRef.current = value;
  });

  const contextValue = useMemo(
    () => ({
      targetMessageExtra,
      iframeMode,
      galaxyModule,
      isGalaxy,
      dialogRef: dialogueRef.current,
      setDialogRef,
      singleTransToSysLang: singleOrBatchTrans,
      clickQuote: handleQuoteClick,
      showFeedback,
      setContextMenuMessage,
      setContextMenuSingleType,
      showContextMenu,
      videoTextState,
      setVideoTextState,
      highlightMsg,
      hasQAPairMounted: hasQAPairMountedRef.current,
      setHasQAPairMounted,
      hideFooter: false,
    }),
    [
      galaxyModule,
      handleQuoteClick,
      highlightMsg,
      iframeMode,
      isGalaxy,
      setDialogRef,
      setHasQAPairMounted,
      showContextMenu,
      showFeedback,
      singleOrBatchTrans,
      targetMessageExtra,
      videoTextState,
    ],
  );

  useEffect(() => {
    const assistantId = auth?.payload?.id;
    const convId = conversation?.conversationId;
    if (convId && assistantId && nimStatus === 'Connected') {
      // 会话优先权
      sendConvOpe(convId, assistantId, 'OPEN').catch(() => {});

      return () => {
        sendConvOpe(convId, assistantId, 'CLOSE').catch(() => {});
      };
    }
    return noop;
  }, [auth?.payload?.id, conversation?.conversationId, nimStatus]);

  const main = isGalaxy ? (
    <SimpleChat
      className={className}
      contextMenu={contextMenu}
      conversation={conversation}
      isGalaxy={isGalaxy}
      iframeMode={iframeMode}
      filterMessages={filterMessages}
      preventPosScroll={preventPosScroll}
    />
  ) : (
    <AdvancedChat
      conversation={conversation}
      className={className}
      contextMenu={contextMenu}
      showSideFoldBtn={showSideFoldBtn}
      forceAcquireControlReason={forceAcquireControlReason}
      isGalaxy={isGalaxy}
      iframeMode={iframeMode}
      filterMessages={filterMessages}
      preventPosScroll={preventPosScroll}
    />
  );

  return (
    <ChatProvider value={contextValue} primaryKey={primaryKey}>
      <Suspense fallback={<Spin spinning />}>
        {main}
        {feedbackMessage && (
          <Feedback
            visible={feedbackVisible}
            onCancel={() => hideFeedback()}
            message={feedbackMessage}
            storeName={conversation?.topBarInfo?.storeName}
          />
        )}
      </Suspense>
    </ChatProvider>
  );
};

export const ChatMain = (props: ChatMainProps) => {
  const { conversationId, isGalaxy } = props;

  useConversationTopBarInfoLoader(conversationId);
  useConversationKnowledgeQueryLoader(conversationId, isGalaxy);

  return (
    <MediaPreviewProvider>
      <ChatMainWithoutMediaContext {...props} />
    </MediaPreviewProvider>
  );
};
